/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import {useEffect, useState} from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';

import PropTypes from 'prop-types';
import { faChevronDown, faChevronUp, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import tokens from '../styles/design-tokens';
import './SectionTogglePanel.scss';

const cardHeaderBg = 'rgba(45, 93, 126, 0.07)';
const cardBodyBg = 'rgba(45, 93, 126, 0.03)';

const StyledTitle = styled.div`
  width: 100%;
  padding: ${props => props.padding || tokens.spacing.accordionPadding};
  font-size: ${(p) => (p.$compact ? tokens.typography.fontSize.xs : tokens.typography.fontSize.sm)};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${props => props.theme?.colors?.primary || tokens.colors.primary};
  letter-spacing: ${(p) => (p.$compact ? '0.04em' : 'normal')};
`;

const StyledSubTitle = styled.div`
  width: 100%;
  padding: ${props => props.padding || tokens.spacing.accordionPadding};
  padding-top: ${(p) => (p.$compact ? '6px' : '10px')};
  padding-bottom: ${(p) => (p.$compact ? '6px' : '10px')};
  font-size: ${(p) => (p.$compact ? '10px' : '11px')};
  color: #6c757d;   /* Muted gray color */
  font-weight: 400;
  line-height: 1.4;
`;

const StyledAccordionContent = styled.div`
    background-color: transparent;
    overflow-y: auto;
    padding: ${(p) => {
        if (p.padding) return p.padding;
        return p.$compact ? '6px 8px 8px 10px' : tokens.spacing.accordionPadding;
    }};
    font-size: ${tokens.typography.fontSize.xs};
    max-height: ${props => props.contentHeight ? `${props.contentHeight}px` : 'auto'};
    height: ${props => props.contentHeight ? `${props.contentHeight}px` : 'auto'};
    overflow-x: ${props => props.overflowX ? 'auto' : 'hidden'};
    
    /* Override FormLabel font-size (Bootstrap's Form.Label has explicit font-size that overrides inheritance) */
    label,
    .form-label,
    .form-control {
        font-size: inherit;
    }
`;

const StyledHorizontalDivider = styled.div`
    height: 1px;
    border-top: 1px solid
        ${(p) =>
            p.$compact ? tokens.colors['gray-200'] : p.theme?.colors?.primary || tokens.colors.primary};
    margin-left: ${(p) => (p.$compact ? '0' : undefined)};
    margin-right: ${(p) => (p.$compact ? '0' : undefined)};
    opacity: ${(p) => (p.$compact ? 0.9 : 1)};
`;

const cardWrapperStyles = css`
  background: ${cardBodyBg};
  border-radius: ${(p) => (p.$square ? '0' : '8px')};
  margin-bottom: ${tokens.spacing.sm};
  overflow: hidden;
`;

const AccordionWrapper = styled.div`
  & + * {
    /* margin-top: ${tokens.spacing.sm}; */
  }
  ${(p) => p.$card && cardWrapperStyles}
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
  background: ${cardHeaderBg};
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  margin-right: 10px;
  color: ${tokens.colors.primary};
  font-size: 14px;
`;

const CardTitleText = styled.div`
  flex: 1;
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.primary};
`;

const CardChevron = styled.div`
  color: ${tokens.colors.primary};
  font-size: 12px;
  display: flex;
  align-items: center;
`;

const AccordionHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  font-size: ${tokens.typography.fontSize.xs};
  width: 100%;
`;

const ExpandIcon = styled.div`
  padding: ${(p) => (p.$compact ? '0.25em 0.4em' : '0.4em 0.7em')};
  font-size: ${(p) => (p.$compact ? '0.75rem' : '1rem')};
  opacity: ${(p) => (p.$compact ? 0.9 : 1)};
`;

const AccordionItem = styled.div`
  overflow: hidden;
  transition: ${props => props.collapsed ? tokens.transitions.accordionCollapse : tokens.transitions.accordion};
  height: ${props => props.collapsed ? '0' : 'auto'};
  max-height: ${props => props.collapsed ? '0' : 'none'};
  ${(p) => p.$card && !p.collapsed && !p.$square && css`
    border-radius: 0 0 8px 8px;
  `}
`;

function SectionTogglePanel(props) {
    const {
        overflowX = true,
        subTitle,
        appearance = 'default',
        icon,
        square = false,
    } = props;
    const compact = appearance === 'compact';
    const card = appearance === 'card';
    const [expand, setExpand] = useState(props.isExpand ? props.isExpand : false);

    const handleOnClick = (value) => {
        setExpand(value);
        props.handleOnClick(value);
    };

    useEffect(() => {
        setExpand(props.isExpand);
    }, [props.isExpand]);

    return (
        <AccordionWrapper
            $card={card}
            $square={square}
            className={compact ? 'accordion-section--compact' : undefined}
        >
            {card ? (
                <CardHeaderRow onClick={() => handleOnClick(!expand)}>
                    {icon && (
                        <CardIcon>
                            <FontAwesomeIcon icon={icon} />
                        </CardIcon>
                    )}
                    <CardTitleText>{props.title}</CardTitleText>
                    <CardChevron>
                        <FontAwesomeIcon icon={expand ? faChevronUp : faChevronDown} />
                    </CardChevron>
                </CardHeaderRow>
            ) : (
                <>
                    <div
                        className={classNames({
                            "sectionAccordionTitle": true,
                            "open": expand,
                            "sectionAccordionTitle--compact": compact,
                        })}
                    >
                        <AccordionHeaderContent
                            onClick={() => handleOnClick(!expand)}
                        >
                            <StyledTitle padding={props.padding} $compact={compact}>{props.title}</StyledTitle>
                        </AccordionHeaderContent>
                        <ExpandIcon
                            onClick={() => handleOnClick(!expand)}
                            $compact={compact}
                        >
                            {compact ? (
                                <FontAwesomeIcon icon={expand ? faChevronUp : faChevronDown} />
                            ) : expand ? (
                                <FontAwesomeIcon icon={faMinus} />
                            ) : (
                                <FontAwesomeIcon icon={faPlus} />
                            )}
                        </ExpandIcon>
                    </div>
                    <StyledHorizontalDivider $compact={compact} />
                </>
            )}
            <AccordionItem collapsed={!expand} $card={card} $square={square}>
                {subTitle && (
                    <StyledSubTitle padding={props.padding} $compact={compact}>
                        {subTitle}
                    </StyledSubTitle>
                )}
                <StyledAccordionContent
                    contentHeight={props.contentHeight}
                    overflowX={overflowX}
                    padding={props.padding}
                    $compact={compact}
                >
                    {props.children}
                </StyledAccordionContent>
            </AccordionItem>
        </AccordionWrapper>
    );
}

SectionTogglePanel.propTypes = {
    contentHeight: PropTypes.number,
    isExpand: PropTypes.bool,
    handleOnClick: PropTypes.func,
    padding: PropTypes.string,
    subTitle: PropTypes.string,
    appearance: PropTypes.oneOf(['default', 'compact', 'card']),
    icon: PropTypes.object,
    square: PropTypes.bool,
};

SectionTogglePanel.defaultProps = {
    handleOnClick: (e) => e,
};

export default SectionTogglePanel;
